import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { UserService } from '@shared/services/user.service';
import { Observable } from 'rxjs';
import { IConfiguration, IInfoColumn, IStatusColumn } from './models/production-plan.models';

@Injectable({
	providedIn: 'root',
})
export class ProductionPlanConfigurationService {
	private url = `${environment.awsEnvUri}/reporting/productionplans`;

	constructor(
		private http: HttpClient,
		private userService: UserService,
	) {}

	get(): Observable<IConfiguration[]> {
		return this.http.get<IConfiguration[]>(this.url, {
			headers: this.userService.getHeaders(),
		});
	}

	getById(id: string): Observable<IConfiguration> {
		return this.http.get<IConfiguration>(`${this.url}/${id}`, {
			headers: this.userService.getHeaders(),
		});
	}

	create(configuration: IConfiguration): Observable<IConfiguration> {
		const sql = configuration.orderPositionSql;
		const request: IConfigurationRequest = {
			alias: configuration.alias,
			description: configuration.description,
			infoColumns: configuration.infoColumns,
			statusColumns: configuration.statusColumns.map((col) => ({
				title: col.title,
				terminalIds: col.terminalIds,
				planLinksIds: col.planLinksIds,
			})),
			orderPositionSql: this.base64EncodeUnicode(sql),
		};
		return this.http.post<IConfiguration>(this.url, request, {
			headers: this.userService.getHeaders(),
		});
	}

	update(configuration: IConfiguration): Observable<IConfiguration> {
		const sql = configuration.orderPositionSql;
		const request: IConfigurationRequest = {
			alias: configuration.alias,
			description: configuration.description,
			infoColumns: configuration.infoColumns,
			statusColumns: configuration.statusColumns.map((col) => ({
				title: col.title,
				terminalIds: col.terminalIds,
				planLinksIds: col.planLinksIds,
			})),
			orderPositionSql: this.base64EncodeUnicode(sql),
		};
		return this.http.put<IConfiguration>(`${this.url}/${configuration.id}`, request, {
			headers: this.userService.getHeaders(),
		});
	}

	base64EncodeUnicode(str: string): string {
		const utf8Bytes = new TextEncoder().encode(str);
		let binary = '';
		utf8Bytes.forEach((byte) => {
			binary += String.fromCharCode(byte);
		});
		return btoa(binary);
	}

	delete(id: string): Observable<void> {
		return this.http.delete<void>(`${this.url}/${id}`, {
			headers: this.userService.getHeaders(),
		});
	}
}

export interface IConfigurationRequest {
	alias: string;
	description: string;
	infoColumns: IInfoColumn[];
	statusColumns: IStatusColumn[];
	orderPositionSql?: string;
}
